import React, { useState, useEffect, useRef } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { isMobileOrTablet } from "../Utils";
import SearchBar from "./BannerSearchBar/SearchBar";
import "./index.css";


const Banner = ({ screenWidth, angelaiUrl }) => {
  const isMobile = isMobileOrTablet();
  const bannerRef = useRef(null);
  const [showGradients, setShowGradients] = useState(false);
  const [loadingStrategy, setLoadingStrategy] = useState("none");

  const isIOS = () => {
    return (
      [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod",
      ].includes(navigator.platform) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  };

  const isIOSDevice = React.useMemo(() => isIOS(), []);

  // Multi-strategy gradient loading
  useEffect(() => {
    let timers = [];
    let observers = [];

    const loadGradients = (strategy) => {
      setLoadingStrategy(strategy);
      setShowGradients(true);
    };

    // Strategy 1: Wait for CLS measurement period (5+ seconds)
    const clsTimer = setTimeout(() => {
      if (!showGradients) {
        loadGradients("cls-safe");
      }
    }, 6000); // 6 seconds to be extra safe
    timers.push(clsTimer);

    // Strategy 2: Load after user interaction
    const handleUserInteraction = () => {
      if (!showGradients) {
        // Small delay to ensure the interaction is processed first
        const interactionTimer = setTimeout(() => {
          loadGradients("user-interaction");
        }, 200);
        timers.push(interactionTimer);
      }
    };

    // Strategy 3: Load when user scrolls past the banner
    const handleScroll = () => {
      if (!showGradients && bannerRef.current) {
        const bannerRect = bannerRef.current.getBoundingClientRect();
        const bannerBottom = bannerRect.bottom;

        // If user has scrolled past 50% of the banner
        if (bannerBottom < window.innerHeight * 0.5) {
          loadGradients("scroll-trigger");
        }
      }
    };

    // Strategy 4: Load after Core Web Vitals APIs confirm measurement
    const loadAfterWebVitals = () => {
      // Check if web-vitals library is available
      if (typeof window !== "undefined" && window.webVitals) {
        window.webVitals.getCLS((metric) => {
          // CLS measurement received, safe to load gradients
          if (!showGradients) {
            const webVitalsTimer = setTimeout(() => {
              loadGradients("web-vitals-complete");
            }, 500);
            timers.push(webVitalsTimer);
          }
        });
      }
    };

    // Strategy 5: IntersectionObserver for when banner leaves viewport
    const createIntersectionObserver = () => {
      if (bannerRef.current && "IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // When banner is less than 30% visible, load gradients
              if (entry.intersectionRatio < 0.3 && !showGradients) {
                loadGradients("intersection-observer");
              }
            });
          },
          { threshold: [0.3] }
        );

        observer.observe(bannerRef.current);
        observers.push(observer);
      }
    };

    // Initialize strategies based on conditions
    const initializeStrategies = () => {
      // Always set the CLS timer as fallback
      // Already set above

      // User interaction listeners
      const events = ["click", "scroll", "touchstart", "keydown"];
      events.forEach((event) => {
        document.addEventListener(event, handleUserInteraction, {
          once: true,
          passive: true,
        });
      });

      // Scroll listener for banner scroll-past detection
      window.addEventListener("scroll", handleScroll, { passive: true });

      // Web Vitals strategy
      loadAfterWebVitals();

      // Intersection Observer strategy
      createIntersectionObserver();
    };

    // Wait for page to be ready before initializing
    if (document.readyState === "complete") {
      initializeStrategies();
    } else {
      window.addEventListener("load", initializeStrategies);
    }

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach((timer) => clearTimeout(timer));

      // Clear all observers
      observers.forEach((observer) => observer.disconnect());

      // Remove event listeners
      const events = ["click", "scroll", "touchstart", "keydown"];
      events.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction);
      });
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("load", initializeStrategies);
    };
  }, [showGradients]);

  // Debug logging (remove in production)
  useEffect(() => {
    if (showGradients) {
      console.log(`Gradients loaded via strategy: ${loadingStrategy}`);
    }
  }, [showGradients, loadingStrategy]);

  // Memoize styles to prevent recalculation on re-renders
  const bannerStyle = React.useMemo(
    () => ({
      textAlign: "center",
      color: "#000",
      position: "relative",
      paddingTop: "1rem",
      paddingBottom: isMobile ? "2rem" : "3rem",
      zIndex: 2,
      backgroundColor: "#FFF3EE",
      overflow: "hidden",
    }),
    [isMobile]
  );

  const textStyle = React.useMemo(
    () => ({
      fontSize: isMobile
        ? isIOS()
          ? "clamp(24px, 6.8vw, 32px)"
          : "clamp(29px, 8.125vw, 32px)"
        : "70px",
      fontFamily: "Poppins",
      fontWeight: 900,
      maxWidth: "870px",
      margin: isMobile ? "4rem auto 0px" : "0rem auto 0rem",
      lineHeight: "1.5",
      transform: "translateZ(0)",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
      WebkitTransformStyle: "preserve-3d",
    }),
    [isMobile, isIOSDevice]
  );

  const safeStyle = React.useMemo(
    () => ({
      fontFamily: "Poppins",
      fontSize: isMobile
        ? isIOS()
          ? "clamp(18px, 6.5vw, 32px)"
          : "clamp(23px, 6.6vw, 32px)"
        : "53px",
      lineHeight: "1.7",
      marginBottom: isMobile ? "8rem" : "10rem",
      transform: "translateZ(0)",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
      WebkitTransformStyle: "preserve-3d",
    }),
    [isMobile, isIOSDevice]
  );

  // Style for gradient circles visibility - keep your existing approach
  const gradientVisibility = showGradients ? 1 : 0;

  // Memoize SearchBar component to prevent unnecessary re-renders
  const MemoizedSearchBar = React.useMemo(
    () => <SearchBar screenWidth={screenWidth} angelaiUrl={angelaiUrl} />,
    [screenWidth, angelaiUrl]
  );

  return (
    <>
      <div id="revamp-banner" style={bannerStyle} ref={bannerRef}>
        {/* Conditionally render gradients with your existing implementation */}
        {showGradients && (
          <>
            {/* <div
              className="gradient-circle-backgroundlava circle-1"
              style={{
                opacity: gradientVisibility,
                transition: "opacity 0.5s ease-in-out",
              }}
            ></div> */}
            {/* <div
              className="gradient-circle-backgroundlava circle-2"
              style={{
                opacity: gradientVisibility,
                transition: "opacity 0.5s ease-in-out",
              }}
            ></div> */}
          </>
        )}

        {/* Main Content */}
        <Container className="Banner-container">
          <Row>
            <Col>
              <h2 style={textStyle} className="text-style bannerangelaih2">
              Low Rate and Low Payment Mortgages by AngelAi.Life
              </h2>
              <h3 className="safe bannerangelaih3" style={safeStyle}>
              Enjoy Life in Your Dream Home Fast, Easy, and Secure
              </h3>
            </Col>
          </Row>
        </Container>

        {/* Use memoized SearchBar */}
        {MemoizedSearchBar}

        {/* <div>
          <BannerVideoSection angelaiUrl={angelaiUrl} />
        </div> */}
      </div>
    </>
  );
};

export default React.memo(Banner);
